import { CloseButton, SaveButton } from '@ui/shared/Button';
import isEmpty from 'lodash/isEmpty';
import * as React from 'react';
import { IQuadrantReportSettings } from '../../../../data/providers/settings/reports/quadrantReportSettingsProvider';
import { ISettingsTemplate } from '../../../../data/providers/settings/reports/types';
import {
  createQuadrantTemplate,
  updateQuadrantTemplate,
} from '../../../../data/writers/settings/reports/quadrantReportSettings';
import { Row } from '../../../layout/Row';
import { CardBody } from '../../../shared/card/CardBody';
import {
  CardFooter,
  CardFooterLeft,
  CardFooterRight,
} from '../../../shared/card/CardFooter';
import {
  CardHeader,
  CardHeaderLeft,
  CardHeaderRight,
} from '../../../shared/card/CardHeader';
import { ErrorMessage } from '../../../shared/form/ErrorMessage';
import { LinearLoading } from '../../../shared/loading/LinearLoading';
import {
  InnerPopupCard,
  PopupCardContent,
} from '../../../shared/popups/PopupCard';
import { LeftColumn } from '../../utils/settings/LeftColumn';
import { RightColumn } from '../../utils/settings/RightColumn';
import { TemplateMetadata } from '../../utils/settings/TemplateMetadata';
import { ITemplateFormProps } from '../../utils/settings/types';
import { AxisForm } from './AxisForm';

export const TemplateForm = (props: ITemplateFormProps): React.ReactElement => {
  const [template, setTemplate] = React.useState<
    Partial<ISettingsTemplate<IQuadrantReportSettings>>
  >(props.template as ISettingsTemplate<IQuadrantReportSettings>);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();

  /**
   * @description handles change to the currently selected template's description
   * @param description
   */
  const templateDescriptionChangeHandler = (description: string): void => {
    setTemplate({
      ...template,
      description,
    });
  };

  /**
   * @description handles change to the currently selected template's name
   * @param name
   */
  const templateNameChangeHandler = (name: string): void => {
    setTemplate({
      ...template,
      name,
    });
  };

  /**
   * @description saves the change to database
   */
  const handleSave = async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      if (props.template && props.template?.id) {
        await updateQuadrantTemplate(
          template as ISettingsTemplate<IQuadrantReportSettings>,
          props.isSelected
        );
      } else {
        await createQuadrantTemplate(template);
      }

      props.onClose();
    } catch (e) {
      setLoading(false);
      setError('Something went wrong saving the template.');
    }
  };

  /**
   * @description handles changes to selected metrics
   * @param mMetrics
   */
  const axisChangeHandler = (mSettings: IQuadrantReportSettings): void => {
    setTemplate({
      ...template,
      settings: {
        ...template?.settings,
        ...mSettings,
      },
    });
  };

  const isValid = React.useMemo((): boolean => {
    if (isEmpty(template?.name)) return false;
    if (isEmpty(template?.settings)) return false;
    if (isEmpty(template?.settings?.xAxis)) return false;
    if (isEmpty(template?.settings?.yAxis)) return false;
    return true;
  }, [JSON.stringify(template)]);

  return (
    <InnerPopupCard onClose={props.onClose} size='medium' noClickOutside>
      <PopupCardContent>
        <CardHeader surface={8}>
          <CardHeaderLeft>
            <h6>
              {props.template
                ? `Edit ${props.template.name}`
                : 'Create Quadrant Settings Template'}
            </h6>
          </CardHeaderLeft>
          <CardHeaderRight>
            <CloseButton onClick={props.onClose} />
          </CardHeaderRight>
        </CardHeader>
        <CardBody>
          {loading && <LinearLoading />}
          <Row shouldCollapse>
            <LeftColumn>
              <TemplateMetadata
                step={1}
                name={template?.name}
                description={template?.description}
                onTemplateDescriptionChange={templateDescriptionChangeHandler}
                onTemplateNameChange={templateNameChangeHandler}
              />
            </LeftColumn>
            <RightColumn>
              <AxisForm
                step={2}
                axisName='xAxis'
                settings={template?.settings}
                onSettingsChange={axisChangeHandler}
              />

              <AxisForm
                step={3}
                axisName='yAxis'
                settings={template?.settings}
                onSettingsChange={axisChangeHandler}
              />
            </RightColumn>
          </Row>
        </CardBody>
        <CardFooter surface={8}>
          <CardFooterLeft>
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </CardFooterLeft>
          <CardFooterRight>
            <SaveButton onClick={handleSave} disabled={loading || !isValid} />
          </CardFooterRight>
        </CardFooter>
      </PopupCardContent>
    </InnerPopupCard>
  );
};
